import { ApiError, getApiBaseUrl } from "./api";
import type { ClientType } from "./types";

function buildUrl(path: string) {
  return new URL(path, `${getApiBaseUrl()}/`).toString();
}

export interface OrderImportPreview {
  authorizationNumber: string;
  deliveryDate: string | null;
  deliveryDateConsistent: boolean;
  existingAuthorization: ExistingAuthorization | null;
  supplier: Supplier;
  billing: PartyChoice;
  charge: PartyChoice;
  deliveryUnit: DeliveryUnitBlock;
  items: ItemPreview[];
  priceTableMissing: boolean;
  errors: string[];
  warnings: string[];
}

export interface ExistingAuthorization {
  orderId: string;
  status: string;
  clientName: string;
  createdAt: string | null;
}

export interface Supplier {
  name: string | null;
  cnpj: string | null;
}

export interface PartyChoice {
  companyName: string | null;
  cnpj: string | null;
  address: string | null;
  district: string | null;
  city: string | null;
  postalCode: string | null;
  existingCustomerId: string | null;
  existingCustomerEmail: string | null;
  /** Populated when the BE matched a customer by CNPJ — used as a fallback when the file
   *  itself does not carry a company name (e.g. XML billing/charge blocks). */
  existingCustomerName: string | null;
}

export interface DeliveryUnitBlock {
  externalCode: string | null;
  name: string | null;
  address: string | null;
  district: string | null;
  city: string | null;
  postalCode: string | null;
  deliveryWindowText: string | null;
  email: string | null;
  existingId: string | null;
}

export interface ItemPreview {
  lineNumber: number;
  externalCode: string | null;
  descriptionFromFile: string | null;
  unitFromFile: string | null;
  quantity: number | null;
  deliveryDate: string | null;
  mappedProductId: string | null;
  mappedProductName: string | null;
  mappedUnitId: string | null;
  mappedUnitName: string | null;
  resolvedUnitPrice: number | null;
  orphan: boolean;
  suggestions: ProductSuggestion[];
  suggestedCut: string | null;
  availableCuts: string[];
  /** Unidades permitidas do produto mapeado (nome). Vazio quando o item não tem produto. */
  availableUnits: string[];
  /**
   * `true` quando produto/corte/unidade vieram de um mapeamento previamente salvo
   * (operador marcou "mapear sempre" no passado). A UI usa para já pré-marcar a checkbox
   * "mapear sempre" e sinalizar visualmente que a linha foi pré-preenchida.
   */
  mappingRemembered: boolean;
}

export interface ProductSuggestion {
  productId: string;
  name: string;
}

export interface OrderImportConfirmRequest {
  authorizationNumber: string;
  overwriteExisting: boolean;
  deliveryDate: string;
  notes: string | null;
  customer: CustomerChoiceRequest;
  deliveryUnit: DeliveryUnitChoiceRequest;
  items: ItemConfirmRequest[];
}

export interface CustomerChoiceRequest {
  existingCustomerId: string | null;
  newCustomer: NewCustomerRequest | null;
}

export interface NewCustomerRequest {
  email: string;
  companyName: string | null;
  tradeName: string | null;
  cnpj: string | null;
  phone: string | null;
  contactName: string | null;
  address: string | null;
  clientType: ClientType | null;
}

export interface DeliveryUnitChoiceRequest {
  existingDeliveryUnitId: string | null;
  newDeliveryUnit: NewDeliveryUnitRequest | null;
}

export interface NewDeliveryUnitRequest {
  externalCode: string;
  name: string;
  address: string | null;
  district: string | null;
  city: string | null;
  postalCode: string | null;
  deliveryWindowText: string | null;
  email: string | null;
}

export interface ItemConfirmRequest {
  externalCode: string | null;
  descriptionFromFile: string | null;
  quantity: number;
  catalogProductId: string;
  catalogUnitId: string | null;
  /** Unidade escolhida pelo operador (nome). O BE resolve e valida contra o produto. */
  unitName: string | null;
  cut: string | null;
  observation: string | null;
  rememberMapping: boolean;
}

export type ImportSourceType = "PDF" | "XML";
export type ImportMessageStatus = "VALID" | "WRONG_FLOW" | "PARSE_ERROR";

export interface OrderImportMessage {
  index: number;
  externalOrderNumber: string | null;
  status: ImportMessageStatus;
  /** Populated when status === "VALID". */
  payload: OrderImportPreview | null;
  /** Populated when status === "WRONG_FLOW". */
  wrongFlowReason: string | null;
  /** Populated when status === "PARSE_ERROR". */
  errorMessage: string | null;
  /**
   * Non-null when the message is VALID but flagged as an ALTERACAO=S AF imported through the
   * new-AF flow. The import is allowed, but the operator must confirm a warning dialog first.
   */
  alterationWarning: string | null;
}

export interface OrderImportEnvelope {
  sourceType: ImportSourceType;
  messages: OrderImportMessage[];
}

/**
 * Uploads a PDF or XML and returns the parsed envelope. PDF uploads always come back as a
 * single-message envelope; XML uploads may carry multiple messages, each independently flagged
 * VALID / WRONG_FLOW / PARSE_ERROR so the wizard can show per-pedido status.
 */
export async function previewOrderImport(file: File): Promise<OrderImportEnvelope> {
  const form = new FormData();
  form.append("file", file);

  const response = await fetch(buildUrl("/api/v1/admin/orders/import/preview"), {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = details && typeof details === "object" && "message" in details
      ? String((details as { message: unknown }).message)
      : "Não foi possível ler o arquivo.";
    throw new ApiError(message, response.status, details);
  }

  return response.json() as Promise<OrderImportEnvelope>;
}

/**
 * Imports one message from the previously-uploaded file. The same File is sent again so the
 * server can re-parse and locate the message by authorizationNumber — keeps the endpoint
 * stateless across preview→confirm. Call once per pedido the operator chose to import.
 */
export async function confirmOrderImport(
  file: File,
  request: OrderImportConfirmRequest,
): Promise<{ id: string }> {
  const form = new FormData();
  form.append("file", file);
  form.append(
    "request",
    new Blob([JSON.stringify(request)], { type: "application/json" }),
  );

  const response = await fetch(buildUrl("/api/v1/admin/orders/import/confirm"), {
    method: "POST",
    credentials: "include",
    body: form,
  });

  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = details && typeof details === "object" && "message" in details
      ? String((details as { message: unknown }).message)
      : "Falha ao confirmar importação.";
    throw new ApiError(message, response.status, details);
  }

  return response.json() as Promise<{ id: string }>;
}
